/**
 * billing.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Client-side helpers for the Paystack checkout flow.
 *
 * Money is NEVER computed here for the actual charge — the paystack-initialize
 * edge function owns the authoritative amount. The display tables below exist
 * only to render prices in the UI; they must stay in sync with
 * supabase/functions/_shared/prices.ts.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { supabase } from "@/integrations/supabase/client";

/**
 * Master switch for online checkout. OFF until the Paystack API keys are
 * configured (frontend gate; the edge functions also refuse without their
 * secret). Flip on by setting VITE_BILLING_ENABLED="true" in the environment.
 * While off, the "Manage plan" / "Renew" buttons and the Pay action are
 * disabled so users don't hit a half-configured checkout.
 */
export const BILLING_ENABLED = import.meta.env.VITE_BILLING_ENABLED === "true";

export type Currency = "NGN" | "USD";
export type Tier = "regular" | "enterprise";
export type Period = "monthly" | "annual";

export const CURRENCY_SYMBOL: Record<Currency, string> = { NGN: "₦", USD: "$" };

/** One-time setup fee in whole currency units (display only). */
export const SETUP_FEE_DISPLAY: Record<Currency, number> = {
  NGN: 50_000,
  USD: 30,
};

/** Plan prices in whole currency units (display only). */
export const PLAN_DISPLAY: Record<Currency, Record<Tier, Record<Period, number>>> = {
  NGN: {
    regular: { monthly: 5_000, annual: 50_000 },
    enterprise: { monthly: 10_000, annual: 100_000 },
  },
  USD: {
    regular: { monthly: 5, annual: 60 },
    enterprise: { monthly: 10, annual: 120 },
  },
};

export const TIER_LABEL: Record<Tier, string> = {
  regular: "Regular",
  enterprise: "Enterprise",
};

/** Format a whole-unit amount with its currency symbol, e.g. ₦50,000 / $30. */
export function formatMoney(currency: Currency, amount: number): string {
  return `${CURRENCY_SYMBOL[currency]}${amount.toLocaleString()}`;
}

/**
 * Compute the display total for the UI (plan + optional setup fee).
 * Mirrors getCheckoutAmount() on the server but in whole units.
 */
export function displayTotal(
  currency: Currency,
  tier: Tier,
  period: Period,
  includeSetupFee: boolean,
): number {
  const base = PLAN_DISPLAY[currency][tier][period];
  return includeSetupFee ? base + SETUP_FEE_DISPLAY[currency] : base;
}

export interface InitializeResult {
  authorization_url: string;
  access_code: string;
  reference: string;
}

/**
 * Kick off a Paystack checkout for an existing host. Returns the hosted
 * authorization URL the caller should redirect the browser to.
 */
export async function startCheckout(params: {
  tier: Tier;
  period: Period;
  currency: Currency;
  email: string;
}): Promise<InitializeResult> {
  // The host is authenticated server-side from this token; we never send a hostId.
  const sessionToken = localStorage.getItem("hostSessionToken");
  const { data, error } = await supabase.functions.invoke<InitializeResult & { error?: string }>(
    "paystack-initialize",
    { body: { ...params, sessionToken } },
  );
  if (error) throw new Error(error.message || "Could not start checkout");
  if (!data || (data as any).error) {
    throw new Error((data as any)?.error || "Could not start checkout");
  }
  if (!data.authorization_url) throw new Error("No checkout URL returned");
  return data;
}

export interface VerifyResult {
  status: "success" | "failed" | "pending";
  subscription_level: string | null;
  subscription_end_date: string | null;
}

/**
 * Verify a transaction by reference after the redirect back to /billing/callback.
 * Idempotent server-side, so it's safe even if the webhook already settled it.
 */
export async function verifyPayment(reference: string): Promise<VerifyResult> {
  const sessionToken = localStorage.getItem("hostSessionToken");
  const { data, error } = await supabase.functions.invoke<VerifyResult & { error?: string }>(
    "paystack-verify",
    { body: { reference, sessionToken } },
  );
  if (error) throw new Error(error.message || "Could not verify payment");
  if (!data || (data as any).error) {
    throw new Error((data as any)?.error || "Could not verify payment");
  }
  return data;
}
